class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.updateFreeShippingProgress();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const hasItems = Array.isArray(parsedState.items) ? parsedState.items.length > 0 : parsedState.item_count !== 0;
    this.classList.toggle('is-empty', !hasItems);
    this.productId = parsedState.id;

    if (parsedState.sections && parsedState.sections['cart-drawer']) {
      const html = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
      const sourceCartDrawer = html.querySelector('cart-drawer');
      const sourceCartDrawerBody = html.querySelector('#CartDrawer');
      const targetCartDrawerBody = this.querySelector('#CartDrawer');

      if (sourceCartDrawer) this.classList.toggle('is-empty', sourceCartDrawer.classList.contains('is-empty'));
      if (sourceCartDrawerBody && targetCartDrawerBody) targetCartDrawerBody.innerHTML = sourceCartDrawerBody.innerHTML;
    }

    if (parsedState.sections && parsedState.sections['cart-icon-bubble']) {
      const cartIcon = document.getElementById('cart-icon-bubble');
      const iconHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble']);
      if (cartIcon && iconHTML !== null) cartIcon.innerHTML = iconHTML;
    }

    this.querySelector('.drawer__inner')?.classList.remove('is-empty');

    setTimeout(() => {
      this.bindOverlay();
      this.applyLayoutConstraints();
      this.resetScrollPosition();
      this.updateFreeShippingProgress();
      this.open();
    });
  }

  applyLayoutConstraints() {
    const isEmpty = this.classList.contains('is-empty');
    const cartDrawerBody = this.querySelector('#CartDrawer');
    const drawerInner = this.querySelector('.drawer__inner');
    const drawerItems = this.querySelector('cart-drawer-items');
    const drawerFooter = this.querySelector('.drawer__footer');
    const freeShipping = this.querySelector('.cart-drawer__free-shipping');

    if (cartDrawerBody) {
      cartDrawerBody.style.height = '100%';
      cartDrawerBody.style.maxHeight = '100dvh';
      cartDrawerBody.style.overflow = 'hidden';
    }

    if (drawerInner) {
      drawerInner.style.height = '100%';
      drawerInner.style.maxHeight = '100dvh';
      drawerInner.style.display = 'grid';
      drawerInner.style.gridTemplateRows = isEmpty ? '1fr' : 'auto auto minmax(0, 1fr) auto';
      drawerInner.style.boxSizing = 'border-box';
      drawerInner.style.overflow = 'hidden';
    }

    if (drawerItems) {
      drawerItems.style.gridRow = '3';
      drawerItems.style.display = isEmpty ? 'none' : 'block';
      drawerItems.style.alignSelf = 'stretch';
      drawerItems.style.minHeight = '0';
      drawerItems.style.minBlockSize = '0';
      drawerItems.style.maxHeight = '100%';
      drawerItems.style.overflowX = 'hidden';
      drawerItems.style.overflowY = 'auto';
    }

    if (drawerFooter) {
      drawerFooter.style.gridRow = '4';
      drawerFooter.style.position = 'relative';
      drawerFooter.style.zIndex = '2';
      drawerFooter.style.display = isEmpty ? 'none' : '';
    }

    if (freeShipping) {
      freeShipping.style.gridRow = '2';
      freeShipping.style.display = isEmpty ? 'none' : '';
    }
  }

  resetScrollPosition() {
    [
      this.querySelector('.drawer__inner'),
      this.querySelector('cart-drawer-items'),
      this.querySelector('#CartDrawer'),
    ].forEach((element) => {
      if (element) element.scrollTop = 0;
    });
  }

  updateFreeShippingProgress() {
    const freeShipping = this.querySelector('[data-cart-free-shipping]');
    if (!freeShipping) return;

    const thresholdUsdCents = Number(freeShipping.dataset.thresholdUsdCents || 0);
    const cartTotalCents = Number(freeShipping.dataset.cartTotalCents || 0);
    const currencyRate = Number(Shopify.currency?.rate || 1);
    const activeCurrency = Shopify.currency?.active || 'USD';
    const thresholdCents = Math.round(thresholdUsdCents * currencyRate);
    const remainingCents = thresholdCents - cartTotalCents;
    const progress = thresholdCents > 0 ? Math.min((cartTotalCents / thresholdCents) * 100, 100) : 0;
    const progressElement = freeShipping.querySelector('.cart-drawer__free-shipping-progress');
    const messageElement = freeShipping.querySelector('.cart-drawer__free-shipping-message');

    if (progressElement) progressElement.style.setProperty('--cart-free-shipping-progress', `${progress}%`);
    if (!messageElement) return;

    if (remainingCents > 0) {
      messageElement.innerHTML = `You are <span data-free-shipping-remaining>${this.formatMoney(
        remainingCents,
        activeCurrency
      )}</span> away from <strong>free shipping</strong>.`;
    } else {
      messageElement.innerHTML = 'Your order qualifies for <strong>free shipping</strong>.';
    }
  }

  formatMoney(cents, currency = Shopify.currency?.active || 'USD') {
    const amount = Number(cents) / 100;
    return new Intl.NumberFormat(Shopify.locale || 'en', {
      style: 'currency',
      currency,
    }).format(amount);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => {
      document.querySelector('cart-drawer')?.updateFreeShippingProgress();
    });
  }

  disableLoading(line) {
    super.disableLoading(line);
    requestAnimationFrame(() => {
      document.querySelector('cart-drawer')?.updateFreeShippingProgress();
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
